import { useState, useEffect } from 'react';
import {
    collection, query, orderBy, getDocs,
    getFirestore, limit, Timestamp, where
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Users, Search, Clock, Shield, Monitor,
    Maximize, Mail, User as UserIcon, LogIn, Activity
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { UserActivityFeed } from './UserActivityFeed';

export default function AdminUsers() {
    const [users, setUsers] = useState<UserSettings[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const db = getFirestore(getApp());
            const usersRef = collection(db, 'users');

            // Sort by lastLogin (if available) or dateUpdated or dateCreated
            // Since lastLogin is new, we fallback to dateUpdated
            const q = query(usersRef, orderBy('lastLogin', 'desc'), limit(100));
            const snapshot = await getDocs(q);

            const items = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    lastLogin: data.lastLogin instanceof Timestamp ? data.lastLogin.toDate() : (data.lastLogin ? new Date(data.lastLogin) : null),
                    dateCreated: data.dateCreated instanceof Timestamp ? data.dateCreated.toDate() : new Date(data.dateCreated),
                    dateUpdated: data.dateUpdated instanceof Timestamp ? data.dateUpdated.toDate() : new Date(data.dateUpdated)
                } as any;
            });

            setUsers(items);
        } catch (error) {
            console.error('Error fetching users:', error);
            // Fallback for case where lastLogin index doesn't exist yet
            try {
                const db = getFirestore(getApp());
                const q = query(collection(db, 'users'), orderBy('dateUpdated', 'desc'), limit(100));
                const snapshot = await getDocs(q);
                const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
                setUsers(items);
            } catch (e) {
                toast.error('Failed to load users');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const filteredUsers = users.filter(user => {
        const query = searchQuery.toLowerCase();
        return (
            (user.displayName?.toLowerCase().includes(query)) ||
            (user.email?.toLowerCase().includes(query)) ||
            (user.userId.toLowerCase().includes(query))
        );
    });

    const getDeviceIcon = (device?: string) => {
        if (!device) return <Monitor className="h-3 w-3" />;
        const d = device.toLowerCase();
        if (d.includes('iphone') || d.includes('android') || d.includes('mobile'))
            return <LogIn className="h-3 w-3 rotate-90" />; // Mobile-ish
        if (d.includes('ipad') || d.includes('tablet'))
            return <Maximize className="h-3 w-3" />; // Tablet-ish
        return <Monitor className="h-3 w-3" />;
    };

    return (
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Registered Users
                    </CardTitle>
                </div>
                <div className="flex items-center gap-2 w-64">
                    <div className="relative w-full">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search users..."
                            className="pl-8 h-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Clock className="h-8 w-8 animate-spin text-muted-foreground opacity-20" />
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground italic">
                        No users found.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-muted">
                                    <th className="pb-3 pt-2 font-medium">User</th>
                                    <th className="pb-3 pt-2 font-medium">Last Activity</th>
                                    <th className="pb-3 pt-2 font-medium">Device / Env</th>
                                    <th className="pb-3 pt-2 font-medium text-right">Progress</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-muted/50">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                                        <td className="py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center overflow-hidden border">
                                                    {user.photoURL ? (
                                                        <img src={user.photoURL} alt="" className="h-full w-full object-cover" />
                                                    ) : (
                                                        <UserIcon className="h-5 w-5 text-muted-foreground" />
                                                    )}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="font-bold truncate">
                                                        {user.displayName || 'Anonymous Explorer'}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                                        <Mail className="h-3 w-3" />
                                                        {user.email || 'No email provided'}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground/60 font-mono">
                                                        {user.userId}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="text-xs flex items-center gap-1 font-medium">
                                                    <LogIn className="h-3 w-3 text-green-600" />
                                                    {user.lastLogin ? format(user.lastLogin as any, 'MMM d, h:mm a') : 'N/A'}
                                                </div>
                                                <div className="text-[10px] text-muted-foreground">
                                                    Created: {format(user.dateCreated as any, 'MMM d, yyyy')}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-[10px] flex items-center gap-1 text-muted-foreground truncate max-w-[180px]">
                                                    {getDeviceIcon(user.device)}
                                                    {user.os} / {user.browser}
                                                </div>
                                                <div className="text-[10px] flex items-center gap-1 text-muted-foreground">
                                                    <Shield className="h-3 w-3" />
                                                    {user.device}
                                                </div>
                                                {user.windowSize && (
                                                    <div className="text-[10px] flex items-center gap-1 text-muted-foreground">
                                                        <Maximize className="h-3 w-3" />
                                                        {user.windowSize.width.toLocaleString()} x {user.windowSize.height.toLocaleString()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-4 text-right">
                                            <div className="flex flex-col items-end gap-1.5">
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                        onClick={() => {
                                                            setSelectedUserId(user.userId);
                                                            setSelectedUserName(user.displayName || 'Anonymous Explorer');
                                                        }}
                                                        title="Inspect Activity"
                                                    >
                                                        <Activity className="h-4 w-4" />
                                                    </Button>
                                                    {user.isAnonymous ? (
                                                        <Badge variant="outline" className="text-[10px] h-4 font-normal">Guest</Badge>
                                                    ) : (
                                                        <Badge variant="secondary" className="text-[10px] h-4 font-normal bg-blue-100 text-blue-700">Account</Badge>
                                                    )}
                                                </div>
                                                {user.hasCompletedOnboarding ? (
                                                    <Badge variant="default" className="text-[10px] h-4 bg-green-600">Onboarded</Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="text-[10px] h-4">New</Badge>
                                                )}
                                                {user.selectedPersonaId && (
                                                    <Badge variant="outline" className="text-[10px] h-4">{user.selectedPersonaId}</Badge>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            <Dialog open={!!selectedUserId} onOpenChange={(open) => !open && setSelectedUserId(null)}>
                <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-6 overflow-hidden">
                    <DialogHeader className="pb-4 border-b">
                        <DialogTitle className="flex items-center gap-2">
                            <Activity className="h-5 w-5 text-primary" />
                            Activity Log
                        </DialogTitle>
                        <DialogDescription>
                            Exploration history for <span className="font-semibold text-foreground">{selectedUserName}</span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto pr-2 mt-4 custom-scrollbar">
                        {selectedUserId && <UserActivityFeed userId={selectedUserId} />}
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
